import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from 'react';
import { css } from 'emotion';
import { getBodyEnd, getViewLog, logView, getWeeklyArticleHistory, } from '@guardian/automat-client';
import { isRecurringContributor, getLastOneOffContributionDate, shouldHideSupportMessaging, getArticleCountConsent, getEmail, MODULES_VERSION, } from '@root/src/web/lib/contributions';
import { getForcedVariant } from '@root/src/web/lib/readerRevenueDevUtils';
import { initPerf } from '@root/src/web/browser/initPerf';
import { sendOphanComponentEvent, submitComponentEvent, } from '@root/src/web/browser/ophan/ophan';
import { setAutomat } from '@root/src/web/lib/setAutomat';
import { getCookie } from '../../browser/cookie';
import { useHasBeenSeen } from '../../lib/useHasBeenSeen';
const checkForErrors = (response) => {
    if (!response.ok) {
        throw Error(response.statusText ||
            `ReaderRevenueEpic | An api call returned HTTP status ${response.status}`);
    }
    return response;
};
const sendOphanReminderEvent = (componentId) => {
    const componentEvent = {
        component: {
            componentType: 'ACQUISITIONS_OTHER',
            id: componentId,
        },
        action: 'CLICK',
    };
    window.guardian.ophan.record({ componentEvent });
};
const sendOphanReminderOpenEvent = ({ buttonCopyAsString }) => {
    sendOphanReminderEvent('precontribution-reminder-prompt-clicked');
    sendOphanReminderEvent(`precontribution-reminder-prompt-copy-${buttonCopyAsString}`);
};
const wrapperMargins = css `
	margin: 18px 0;
`;
const buildPayload = async (props) => {
    return {
        tracking: {
            ophanPageId: window.guardian.config.ophan.pageViewId,
            ophanComponentId: '',
            platformId: 'GUARDIAN_WEB',
            clientName: 'dcr',
            referrerUrl: window.location.origin + window.location.pathname,
        },
        targeting: {
            contentType: props.contentType,
            sectionName: props.sectionName || '',
            shouldHideReaderRevenue: props.shouldHideReaderRevenue,
            isMinuteArticle: props.isMinuteArticle,
            isPaidContent: props.isPaidContent,
            tags: props.tags,
            showSupportMessaging: !shouldHideSupportMessaging(props.isSignedIn || false),
            isRecurringContributor: isRecurringContributor(props.isSignedIn || false),
            lastOneOffContributionDate: getLastOneOffContributionDate(),
            epicViewLog: getViewLog(),
            weeklyArticleHistory: getWeeklyArticleHistory(),
            hasOptedOutOfArticleCount: !(await getArticleCountConsent()),
            mvtId: Number(getCookie('GU_mvt_id')),
            countryCode: props.countryCode,
            modulesVersion: MODULES_VERSION,
        },
    }; // Metadata type incorrectly does not include required hasOptedOutOfArticleCount property
};
export const canShow = async ({ isSignedIn, countryCode, contentType, sectionName, shouldHideReaderRevenue, isMinuteArticle, isPaidContent, tags, contributionsServiceUrl, idApiUrl, }) => {
    if (shouldHideReaderRevenue || isPaidContent) {
        // We never serve Reader Revenue epics in this case
        return Promise.resolve({ result: false });
    }
    const dataPerf = initPerf('contributions-epic-data');
    dataPerf.start();
    const forcedVariant = getForcedVariant('epic');
    const queryString = forcedVariant ? `?force=${forcedVariant}` : '';
    const contributionsPayload = await buildPayload({
        isSignedIn,
        countryCode,
        contentType,
        sectionName,
        shouldHideReaderRevenue,
        isMinuteArticle,
        isPaidContent,
        tags,
        contributionsServiceUrl,
        idApiUrl,
    });
    const response = await getBodyEnd(contributionsPayload, `${contributionsServiceUrl}/epic${queryString}`);
    checkForErrors(response);
    const json = await response.json();
    if (!json.data) {
        return { result: false };
    }
    const email = isSignedIn ? await getEmail(idApiUrl) : undefined;
    const { meta, module } = json.data;
    return {
        result: true,
        meta: {
            meta,
            module,
            email,
            onReminderOpen: sendOphanReminderOpenEvent,
        },
    };
};
export const ReaderRevenueEpic = ({ meta, module, email }) => {
    const [Epic, setEpic] = useState();
    const [hasBeenSeen, setNode] = useHasBeenSeen({
        rootMargin: '-18px',
        threshold: 0,
        debounce: true,
    });
    useEffect(() => {
        setAutomat();
        const modulePerf = initPerf('contributions-epic-module');
        modulePerf.start();
        window
            .guardianPolyfilledImport(module.url)
            .then((epicModule) => {
            modulePerf.end();
            setEpic(() => epicModule.ContributionsEpic); // useState requires functions to be wrapped
            sendOphanComponentEvent('INSERT', meta);
        })
            .catch((error) => {
            const msg = `Error importing RR epic: ${error}`;
            // eslint-disable-next-line no-console
            console.log(msg);
            window.guardian.modules.sentry.reportError(new Error(msg), 'rr-epic');
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (hasBeenSeen && meta) {
            // Should only run once
            const { abTestName } = meta;
            logView(abTestName);
            sendOphanComponentEvent('VIEW', meta);
        }
    }, [hasBeenSeen, meta]);
    if (Epic) {
        return (_jsx("div", Object.assign({ ref: setNode, className: wrapperMargins }, { children: _jsx(Epic, Object.assign({}, module.props, { email: email, submitComponentEvent: submitComponentEvent }), void 0) }), void 0));
    }
    return null;
};
